<template>
    <modal
        title="Html Custom Attributes"
        class="select-options"
        header-bg-variant="blue-600"
        header-text-variant="white"
        :hide-header-close="true"
        @ok="onConfirm"
    >
        <form
            ref="form"
            class="select-options__content"
            @submit.prevent
        >
            <header class="select-options__header">
                <div class="select-options__col">
                    <span class="select-options__label">Attribute Name</span>
                </div>

                <div class="select-options__col">
                    <span class="select-options__label">Attribute Value</span>
                </div>
            </header>

            <ul class="select-options__list">
                <li v-for="(attribute, i) in attributes" :key="i" class="select-options__item">
                    <div class="select-options__col">
                        <input
                            class="select-options__input"
                            type="text"
                            placeholder="Add name"
                            :value="attribute.name"
                            required
                            @input="(e) => onInputName(e, i)"
                        >
                    </div>

                    <div class="select-options__col">
                        <input
                            class="select-options__input"
                            type="text"
                            placeholder="Add value"
                            :value="attribute.value"
                            @input="(e) => onInputValue(e, i)"
                        >
                        <button class="select-options__delete" @click="deleteRow(i)">
                            <font-awesome-icon
                                :icon="['fal', 'trash-alt']"
                            />
                        </button>
                    </div>
                </li>
            </ul>

            <button class="select-options__add" type="button" @click="onAddAttribute">
                Add Attribute
            </button>
        </form>

        <template v-slot:modal-footer="slotProps">
            <button class="flex-modal__button flex-modal__button--cancel" @click="slotProps.hide('cancel')">
                Cancel
            </button>

            <button class="flex-modal__button flex-modal__button--continue" @click="onConfirm">
                Update
            </button>
        </template>
    </modal>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { HIDE_MODAL, SET_CHANGED_DATA } from '@/store/types/mutation-types';

export default {
    data() {
        return {
            isValid: true,
            attributes: this.getEmptyAttribute(),
        };
    },

    computed: {
        ...mapState({
            selectedElements: state => state.page.selectedElements,
        }),

        existingAttributes() {
            return !!Object.keys(this.selectedElements[0].htmlCustomAttributesNew).length;
        },
    },

    created() {
        if (this.existingAttributes) {
            this.attributes = this.deserializeAttributes(this.selectedElements[0].htmlCustomAttributesNew);
        }
    },

    methods: {
        ...mapMutations({
            hideModal: HIDE_MODAL,
            setChangedData: SET_CHANGED_DATA,
        }),

        getEmptyAttribute() {
            return [
                { name: '', value: '' },
            ];
        },

        deleteRow(index) {
            this.attributes.splice(index, 1);
        },

        onInputName(event, index) {
            this.attributes[index].name = event.target.value;
        },

        onInputValue(event, index) {
            this.attributes[index].value = event.target.value;
        },

        onAddAttribute() {
            const lastAttribute = this.attributes[this.attributes.length - 1];

            if (!lastAttribute.name || !lastAttribute.value) {
                this.validate();
                return;
            }

            this.attributes.push({ name: '', value: '' });
        },

        onConfirm() {
            this.validate();

            if (this.isValid) {
                this.selectedElements[0].htmlCustomAttributesNew = this.serializeAttributes(this.attributes);
                this.setChangedData(true);
                this.hideModal();
            }
        },

        validate() {
            this.isValid = this.$refs.form.reportValidity();
        },

        serializeAttributes(attributes) {
            return attributes.reduce((nvList, attribute) => {
                nvList[attribute.name] = attribute.value;

                return nvList;
            }, {});
        },

        deserializeAttributes(attributes) {
            if (!this.existingAttributes) {
                return this.getEmptyAttribute();
            }

            return Object.entries(attributes).map((item) => {
                return {
                    name: item[0],
                    value: item[1],
                };
            });
        },
    },
};
</script>

<style lang="scss">
.select-options {
    &__switch {
        display: flex;
        align-items: center;
        padding: 1.5rem 1.5rem 0 1.5rem;

        input {
            position: relative;
            top: -1px;
            margin-right: 0.75em;
        }
    }

    &__content {
        padding: 1.5rem;
    }

    &__section {
        width: 100%;
        margin-bottom: 0.75em;
    }

    &__header {
        display: flex;
        margin-bottom: 0.75em;
    }

    &__label {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--form-labelColor);
    }

    &__item {
        display: flex;

        &:not(:first-of-type) {
            margin-top: 0.75rem;
        }
    }

    &__col {
        width: 50%;
        display: flex;

        &:first-child {
            padding-right: 0.5rem;
        }

        &:not(:first-child):not(:last-child) {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }

        &:last-child {
            padding-left: 0.5rem;
        }
    }

    &__delete {
        margin-left: 10px;
    }

    &__input {
        @extend %input;
    }

    &__add {
        @extend %button-alt;
        @extend %button-compact;
        margin-top: 1.75rem;
    }
}
</style>
